'use client';

import type { HowDidYouHearAboutUsFields } from '@/components/_rebrand/RequestDemo/Confirmation';
import type { RequestDemoFormStepOneFields } from '@/components/_rebrand/RequestDemo/RequestDemoFormStepOne';
import type { RequestDemoFormStepTwoFields } from '@/components/_rebrand/RequestDemo/RequestDemoFormStepTwo';
import { GTM_EVENTS, GTM_VARIABLES } from '@/constants/gtm';
import {
  HubspotCompanySizeOptions,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs,
} from '@/constants/hubspot';
import { OPTIMIZELY_EVENTS } from '@/constants/optimizely';
import { QualifiedExperienceIDs } from '@/constants/qualified';
import { DEMO_REQUESTED_SESSION_KEY } from '@/constants/session-storage';
import { OneTrustContext } from '@/context/onetrust';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import gtm from '@/lib/gtm';
import { checkHubspotEmailDomain } from '@/lib/hubspot';
import optimizely from '@/lib/optimizely';
import {
  amendQualifiedFormData,
  handleQualifiedEventListener,
  saveQualifiedFormData,
  showQualifiedFormExperience,
} from '@/lib/qualified';
import { QualifiedFormData } from '@/types/qualified';
import { FormikProps } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSessionstorageState } from 'rooks';

export enum DemoFormSteps {
  FormStepOne = 'initial-form',
  FormStepTwo = 'secondary-form',
  Scheduler = 'scheduler',
  Confirmation = 'confirmation',
  ConfirmationSubmitted = 'confirmation-submitted',
}

const DEFAULT_MEETING_NOT_OFFERED_VALUES = {
  [HubspotFieldNames.QualifiedMeetingOffered]: 'No',
  [HubspotFieldNames.MostRecentQualifiedMeetingOfferedStatus]: 'No',
  [HubspotFieldNames.QualifiedBookingStatus]: 'Was not shown calendar',
  [HubspotFieldNames.MostRecentQualifiedBookingStatus]: 'Was not shown calendar',
};

const BELOW_COMPANY_SIZE_THRESHOLD_VALUES = {
  ...DEFAULT_MEETING_NOT_OFFERED_VALUES,
  [HubspotFieldNames.QualifiedMeetingNotOfferedReason]: 'Company size below threshold',
};

const EXISTING_CUSTOMER_VALUES = {
  ...DEFAULT_MEETING_NOT_OFFERED_VALUES,
  [HubspotFieldNames.QualifiedMeetingNotOfferedReason]: 'Flagged as existing customer',
};

export type GdprOptInState = {
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

export type useRequestDemoFormResult = {
  currentStep: DemoFormSteps;
  demoStepTwoFormData: RequestDemoFormStepTwoFields | null;
  meetingOffered: boolean;
  stepOneFormik: FormikProps<RequestDemoFormStepOneFields>;
  stepTwoFormik: FormikProps<RequestDemoFormStepTwoFields>;
  howDidYouHearAboutUsFormik: FormikProps<HowDidYouHearAboutUsFields>;
  reOpenQualifiedScheduler: () => void;
  /** Non-null only when the user is in a GDPR region. */
  gdprOptIn: GdprOptInState | null;
  onMeetingBooked: () => void;
};

export const useRequestDemoForm = (): useRequestDemoFormResult => {
  const { isGDPR } = useContext(OneTrustContext);
  const [currentStep, setCurrentStep] = useState<DemoFormSteps>(DemoFormSteps.FormStepOne);
  const [demoStepOneFormData, setdemoStepOneFormData] = useState<RequestDemoFormStepOneFields | null>(null);
  const [demoStepTwoFormData, setdemoStepTwoFormData] = useState<RequestDemoFormStepTwoFields | null>(null);
  const [meetingOffered, setMeetingOffered] = useState<boolean>(false);
  const [, setDidRequestDemo] = useSessionstorageState<boolean>(DEMO_REQUESTED_SESSION_KEY, false);
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState<boolean>(isGDPR ? false : true);
  const meetingOfferedRef = useRef<boolean>(true);
  const hasSavedQualifiedFormData = useRef<boolean>(false);

  const incrementInitialDemoFormStep = (values: RequestDemoFormStepOneFields) => {
    setDidRequestDemo(true);
    setdemoStepOneFormData(values);
    setCurrentStep(DemoFormSteps.FormStepTwo);
  };

  const incrementSecondaryDemoFormStep = (values: RequestDemoFormStepTwoFields, meetingOffered: boolean) => {
    setMeetingOffered(meetingOffered);
    setdemoStepTwoFormData(values);
    if (meetingOffered) {
      setCurrentStep(DemoFormSteps.Scheduler);
    } else {
      setCurrentStep(DemoFormSteps.Confirmation);
    }
  };

  const stepOneSubmit = useFormikHubspotSubmit<RequestDemoFormStepOneFields>({
    formId: HubspotFormIDs.RequestDemoStarted,
    name: 'Request Demo Modal Start',
    content: 'Request Demo Modal Start',
    onSuccess: async (values) => {
      gtm.event(GTM_EVENTS.DEMO_STARTED, {
        [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
        [GTM_VARIABLES.COMPANY_SIZE]: values[HubspotFieldNames.CompanySize],
      });

      optimizely.event(OPTIMIZELY_EVENTS.DEMO_STARTED);

      incrementInitialDemoFormStep(values);
    },
  });

  const stepOneFormik = useCustomFormik<RequestDemoFormStepOneFields>({
    initialValues: {
      [HubspotFieldNames.Email]: '',
      [HubspotFieldNames.CompanySize]: '',
      [HubspotFieldNames.MarketingEmailOptOut]: isGDPR ? true : false,
    },
    onSubmit: stepOneSubmit,
    validate: (values) => {
      const errors: Partial<Record<HubspotFieldNames, string | undefined>> = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        { required: true, allowPersonalEmail: false, allowSharedEmail: false },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        { required: true },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  const stepTwoSubmit = useFormikHubspotSubmit<RequestDemoFormStepTwoFields>({
    formId: HubspotFormIDs.RequestDemo,
    name: 'Request Demo Modal',
    content: 'Request Demo Modal',
    beforeSubmit: async (values) => {
      const { isPayingCustomer } = await checkHubspotEmailDomain(values[HubspotFieldNames.Email]);

      if (isPayingCustomer) {
        Object.assign(values, EXISTING_CUSTOMER_VALUES);
        meetingOfferedRef.current = false;
      } else if (parseInt(values[HubspotFieldNames.CompanySize], 10) < parseInt(HubspotCompanySizeOptions.Small, 10)) {
        Object.assign(values, BELOW_COMPANY_SIZE_THRESHOLD_VALUES);
        meetingOfferedRef.current = false;
      }
    },
    onSuccess: async (values) => {
      gtm.trackDemoRequest({
        [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
        [GTM_VARIABLES.USER_FIRST_NAME]: values[HubspotFieldNames.FirstName],
        [GTM_VARIABLES.USER_LAST_NAME]: values[HubspotFieldNames.LastName],
        [GTM_VARIABLES.USER_PHONE]: values[HubspotFieldNames.Phone],
      });

      optimizely.event(OPTIMIZELY_EVENTS.ANY_GET_STARTED_MODAL_FORM_SUBMIT);
      optimizely.event(OPTIMIZELY_EVENTS.REQUEST_DEMO_SUCCESS);

      incrementSecondaryDemoFormStep(values, meetingOfferedRef.current);
    },
  });

  const stepTwoFormik = useCustomFormik<RequestDemoFormStepTwoFields>({
    enableReinitialize: true,
    initialValues: {
      [HubspotFieldNames.CompanySize]: demoStepOneFormData?.[HubspotFieldNames.CompanySize] ?? '',
      [HubspotFieldNames.Email]: demoStepOneFormData?.[HubspotFieldNames.Email] ?? '',
      [HubspotFieldNames.MarketingEmailOptOut]:
        demoStepOneFormData?.[HubspotFieldNames.MarketingEmailOptOut] ?? (isGDPR ? true : false),
      [HubspotFieldNames.CompanyName]: '',
      [HubspotFieldNames.FirstName]: '',
      [HubspotFieldNames.Industry]: '',
      [HubspotFieldNames.JobTitle]: '',
      [HubspotFieldNames.LastName]: '',
      [HubspotFieldNames.PrimaryUsecase]: '',
      [HubspotFieldNames.Phone]: '',
    },
    onSubmit: stepTwoSubmit,
    validate: (values) => {
      const errors: Partial<Record<HubspotFieldNames, string | undefined>> = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        { required: true, allowPersonalEmail: true, allowSharedEmail: true },
      );

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        { required: true },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        { required: true },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        { required: true },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        { required: true },
      );

      errors[HubspotFieldNames.Phone] = HubspotFieldValidators[HubspotFieldNames.Phone](
        values[HubspotFieldNames.Phone],
        { required: true },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  const howDidYouHearAboutUsFormik = useCustomFormik<HowDidYouHearAboutUsFields>({
    enableReinitialize: true,
    initialValues: {
      [HubspotFieldNames.Email]: demoStepOneFormData?.[HubspotFieldNames.Email] ?? '',
      [HubspotFieldNames.HowDidYouFindFront]: '',
      [HubspotFieldNames.HowDidYouFindFrontOther]: '',
    },
    onSubmit: useFormikHubspotSubmit({
      formId: HubspotFormIDs.HowDidYouHearAboutUs,
      name: 'How did you find Front?',
      content: 'How did you find Front?',
      onSuccess: async () => setCurrentStep(DemoFormSteps.ConfirmationSubmitted),
    }),
  });

  // Qualifed scheduler handling
  const submitQualifiedFormData = async (formFields: RequestDemoFormStepTwoFields) => {
    const qualifiedFormData: QualifiedFormData = {
      email: formFields[HubspotFieldNames.Email],
      first_name: formFields[HubspotFieldNames.FirstName],
      last_name: formFields[HubspotFieldNames.LastName],
      company: formFields[HubspotFieldNames.CompanyName],
      title: formFields[HubspotFieldNames.JobTitle] || '',
      phone_number: formFields[HubspotFieldNames.Phone],
      company_size_form: formFields[HubspotFieldNames.CompanySize],
      industry_self_select: formFields[HubspotFieldNames.Industry],
    };
    // Need to await becuase amendQualifiedFormData makes an API call to get ip_country from our API endpoint at /api/geoip
    const amendedFormData = await amendQualifiedFormData(qualifiedFormData);

    // Save form data to Qualified
    saveQualifiedFormData(amendedFormData);
  };

  const handleQualifiedSchedulerStep = async (demoStepTwoFormData: RequestDemoFormStepTwoFields) => {
    await submitQualifiedFormData(demoStepTwoFormData);
    hasSavedQualifiedFormData.current = true;
    showQualifiedFormExperience(QualifiedExperienceIDs.TestingForm);
    setMeetingOffered(true);
  };

  useEffect(() => {
    if (currentStep !== DemoFormSteps.Scheduler) {
      return;
    }
    // Use hasSavedQualifiedFormData ref to prevent duplicate form submission.
    if (!hasSavedQualifiedFormData.current && demoStepTwoFormData) {
      handleQualifiedSchedulerStep(demoStepTwoFormData);
    }
  }, [currentStep, demoStepTwoFormData, hasSavedQualifiedFormData.current]);

  // Set up an event listener for meeting booked
  useEffect(() => {
    handleQualifiedEventListener((name, data) => {
      if (name === 'Meeting Booked') {
        onMeetingBooked();
      }
    });
  }, []);

  const reOpenQualifiedScheduler = () => {
    if (!demoStepTwoFormData) {
      return;
    }
    handleQualifiedSchedulerStep(demoStepTwoFormData);
  };

  useEffect(() => {
    setMarketingEmailOptIn(isGDPR ? false : true);
    stepOneFormik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, isGDPR ? true : false);
  }, [isGDPR]);

  const handleMarketingOptInToggle = (checked: boolean) => {
    setMarketingEmailOptIn(checked);
    stepOneFormik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, !checked);
  };

  const onMeetingBooked = () => {
    setCurrentStep(DemoFormSteps.Confirmation);
  };

  return {
    currentStep: currentStep,
    demoStepTwoFormData: demoStepTwoFormData,
    meetingOffered: meetingOffered,
    stepOneFormik,
    stepTwoFormik,
    howDidYouHearAboutUsFormik,
    reOpenQualifiedScheduler,
    gdprOptIn: isGDPR ? { checked: marketingEmailOptIn, onToggle: handleMarketingOptInToggle } : null,
    onMeetingBooked,
  };
};
